/* ---------------------------------------------------------------------------
   The client side of the Pages Functions API.

   Every call can fail — this is a tool for people standing on a riverbank with
   one bar of signal — so nothing here throws anything the caller cannot report.
   Failures surface as ApiError with a message fit to put in front of a user; the
   store falls back to the bundled seed when the register call fails.
   --------------------------------------------------------------------------- */

import type {
  Bridge,
  BridgeChange,
  DefectQuantity,
  DefectSighting,
  DefectStatusChange,
  Inspection,
  InspectionState,
  ModelIndex,
  ProgressEntry,
  ProgressState,
  ProgressSummary,
  RegisterDefect,
  SiteDocument,
  SiteIncident,
  SiteItem,
  SiteReturn,
  SiteTest,
  PlantDay,
  PeriodReport,
  TradeCount,
} from '../data/types.ts';

import type { Baseline, BaselineActivity, BaselineHeader, BaselineLink, ScheduleMatch } from '../data/variance.ts';
import type { WorkCalendar } from '../data/calendar.ts';
import type { ContractExtension, ContractHeader } from '../data/contract.ts';
import type { RateTable } from '../data/rates.ts';
import type { StoredWording } from '../data/remedial.ts';
import type { Capability, Person, Role } from '../data/roles.ts';
import type { Phase, PhaseAct, PhaseState, WorksKind } from '../data/phase.ts';
import type { FieldValue, Handover, HandoverCarry, HandoverFieldKey, HandoverKind } from '../data/handover.ts';

/** What GET /api/defects/:id/history answers (LC-62). */
export interface DefectHistory {
  defect: RegisterDefect;
  statuses: DefectStatusChange[];
  sightings: DefectSighting[];
}

const BASE = '/api';

/** How long a single call may take before it is treated as unreachable. */
const TIMEOUT_MS = 12_000;

/**
 * The model index's own budget, and the one call that needed one.
 *
 * GET /api/modeler/models is the most expensive read this client makes — the
 * handler's own comment records six round trips and 6.8 seconds against remote
 * bindings — and it is read while six other per-structure calls are in flight.
 * Against a flat 12 seconds that is a coin toss, and losing the toss used to
 * mean the record stated "No 3D model built" over a bridge with two models.
 *
 * LONGER IS AFFORDABLE NOW AND WAS NOT BEFORE. Until src/core/modelIndexCache.ts
 * the record had nothing to draw while this was outstanding, so a long budget
 * was a long blank panel; now the last index this device was given is already on
 * screen and this call only refreshes it. Nobody waits on it, so it can wait —
 * up to a point, because a socket held open on a dead connection at an abutment
 * is a socket the defect queue is not using.
 */
const INDEX_TIMEOUT_MS = 25_000;

/**
 * Fields are declared and assigned rather than written as constructor parameter
 * properties: the test and snapshot scripts run this file through Node's
 * strip-only type stripping, which does not support that syntax.
 */
export class ApiError extends Error {
  readonly status: number | null;
  /**
   * Which kind of refusal this is, where the endpoint said.
   *
   * `access` — the register does not know who you are any more: no Access
   * session, or one that has expired. Signing in fixes it.
   * `role` — it knows exactly who you are and the answer is no. Signing in
   * again lands the same person on the same refusal; only an administrator can
   * change it.
   *
   * Null for everything else, including every network failure. The distinction
   * is made by the SERVER — see reshapeDenial in functions/lib/access.ts and
   * refuse in functions/lib/roles.ts — rather than by reading the message here,
   * which would break the first time either sentence was reworded.
   */
  readonly reason: 'access' | 'role' | 'credentials' | 'locked' | 'suspended' | 'must-change-password' | null;
  /** Where to send somebody to sign in, where the denial carried it. */
  readonly loginUrl: string | null;

  constructor(
    message: string,
    status: number | null = null,
    detail: { reason?: ApiError['reason']; loginUrl?: string | null } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.reason = detail.reason ?? null;
    this.loginUrl = detail.loginUrl ?? null;
  }
}

/** Shape of the error body every endpoint returns on a 4xx or 5xx. */
interface ErrorBody {
  error?: string;
  reason?: string;
  loginUrl?: string;
}

/* --------------------------- the session --------------------------- */

/**
 * Notified whenever a call is refused because the session is gone.
 *
 * WHY A HOOK AND NOT A STORE IMPORT. This module sits below the store on
 * purpose — src/core/store.ts imports it, the Defect Inspector's own store
 * imports it, and the sync queue imports it. Reaching up into any one of those
 * from here would make this file unusable from the other two, which is exactly
 * the seam FIELD.md's §7 argues for.
 *
 * WHY IT IS CENTRAL AND NOT PER CALL SITE. An expired session shows up on
 * whichever request happens to be in flight when it lapses — a register
 * hydrate, a defect save, a photograph, the model index. There is no one call
 * to check, so the check is here, once, and every screen learns about it the
 * same way.
 */
export interface SessionLost {
  message: string;
  /** Null where the denial carried no login URL — the sign-in screen then falls back. */
  loginUrl: string | null;
}

type SessionListener = (lost: SessionLost) => void;

const sessionListeners = new Set<SessionListener>();

/** Subscribe to session loss. Returns the unsubscribe. */
export function onSessionLost(fn: SessionListener): () => void {
  sessionListeners.add(fn);
  return () => sessionListeners.delete(fn);
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  timeoutMs: number = TIMEOUT_MS,
): Promise<T> {
  // jsdom under the smoke tests has no fetch unless one is stubbed in. Say so
  // plainly rather than failing with "fetch is not a function".
  if (typeof fetch !== 'function') {
    throw new ApiError('No fetch available in this environment.');
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, { ...init, signal: controller.signal });
  } catch (err) {
    // Offline, DNS failure, CORS, or our own abort above.
    const aborted = err instanceof Error && err.name === 'AbortError';
    throw new ApiError(aborted ? 'Request timed out.' : 'Backend unreachable.');
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    let reason: ApiError['reason'] = null;
    let loginUrl: string | null = null;
    try {
      const body = (await response.json()) as ErrorBody;
      if (body.error) detail = body.error;
    if (
        body.reason === 'access' ||
        body.reason === 'role' ||
        body.reason === 'credentials' ||
        body.reason === 'locked' ||
        body.reason === 'suspended' ||
        body.reason === 'must-change-password'
      ) {
        reason = body.reason;
      }
      if (typeof body.loginUrl === 'string') loginUrl = body.loginUrl;
    } catch {
      /* not a JSON error body — keep the status line */
    }

    /* THE SESSION, ANNOUNCED ONCE. Every caller still gets the error thrown at
       it and still handles its own failure; this is the shell being told that the
       failure was not the caller's fault and not the network's. Fired before the
       throw so the notice is up by the time the caller's catch runs. */
    /* A LOST SESSION, AND NOT A REFUSAL. `must-change-password` is also a 403
       from the same guard and must NOT come through here: that person IS signed
       in, and throwing up the sign-in screen would ask them to do the one thing
       they have just done. The store handles it as its own state. */
    if (reason === 'access') {
      for (const fn of sessionListeners) fn({ message: detail, loginUrl });
    }

    throw new ApiError(detail, response.status, { reason, loginUrl });
  }

  /* A write that has nothing to say answers 204 with no body — withdrawing a
     defect is the first of those. Parsing that as JSON fails, and reporting
     "malformed response" for a request that succeeded would send a caller
     looking for a server fault that is not there. */
  if (response.status === 204) return undefined as T;

  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError('Backend returned a malformed response.');
  }
}

/* --------------------------- reads --------------------------- */

/** Who Cloudflare Access says is signed in, as /api/health reports it. */
export interface Identity {
  /** Verified email for a person, null for a service token. */
  email: string | null;
  /** Client ID for a service token, null for a person. */
  serviceToken: string | null;
  /** What an edit made now would be recorded against. */
  author: string;
  /**
   * Every role the register says this identity holds — see src/data/roles.ts.
   * Empty is an account that reads and writes nothing.
   *
   * Null where this deployment does not enforce Access: there is then nobody to
   * hold a role, /api is open, and `can()` reads the null as "permitted" rather
   * than inventing a role to withhold things by. The title bar prints which of
   * the two it is looking at.
   */
  roles: Role[] | null;
  /** Suspended: may do nothing, including read, whatever `roles` holds. */
  suspended: boolean;
  /** Whether it came from the deployment's ADMIN_EMAILS, a grant, or nothing granted. */
  roleSource: 'deployment' | 'granted' | 'default' | null;
  /**
   * The role's capabilities, as the SERVER has them.
   *
   * The client holds the same table in src/data/roles.ts and this is not
   * redundant with it: a browser running a cached older build would otherwise
   * decide what to draw from a map the server has since changed, and the server's
   * copy is the one that matches what it will actually permit. Null with `roles`,
   * and for the same reason.
   */
  capabilities: Capability[] | null;
}

export interface HealthReport {
  ok: boolean;
  checks: Record<string, string>;
  /**
   * Which department's register this session is working in.
   *
   * THE SERVER'S ANSWER, resolved from the signed-in user's own row, and the
   * only one the client acts on — a browser that chose for itself would be a
   * browser drawing one department's branding over another's records. Optional
   * because a deployment running code from before tenancy does not send it, and
   * a field device on a cached build has to keep working: absent resolves to the
   * build's own `VITE_TENANT`, or to Nepal DoR. See src/tenant/registry.ts.
   */
  tenant?: string | null;
  /** Null where this deployment does not enforce Access. */
  identity: Identity | null;
}

export function health(): Promise<HealthReport> {
  return request<HealthReport>('/health');
}

/** The register from D1. */
export async function fetchRegister(): Promise<Bridge[]> {
  const { bridges } = await request<{ bridges: Bridge[] }>('/bridges');
  if (!Array.isArray(bridges)) throw new ApiError('Register response had no bridges array.');
  return bridges;
}

/**
 * The 3D models saved against one structure.
 *
 * Only the index — names, timestamps, sizes. The model states themselves are read
 * by the modeller's own frame through /api/modeler/state, because it is the shim in
 * src/modeler-host/boot.ts that needs them and not this app.
 */
export async function fetchModels(bridgeId: string): Promise<ModelIndex> {
  const body = await request<{
    models: ModelIndex['models'];
    lastSavedAt: string | null;
    totalBytes: number;
  }>(`/modeler/models?bridgeId=${encodeURIComponent(bridgeId)}`, {}, INDEX_TIMEOUT_MS);
  return {
    models: Array.isArray(body.models) ? body.models : [],
    lastSavedAt: body.lastSavedAt ?? null,
    totalBytes: typeof body.totalBytes === 'number' ? body.totalBytes : 0,
  };
}


/**
 * Create an empty saved model for an import to land in.
 *
 * A structure with nothing modelled has nothing to import INTO, and the modeller's
 * own "new bridge" journey cannot run off screen — see the note on the endpoint.
 * So the register makes the shell itself and the import overwrites it.
 */
export async function createModel(
  bridgeId: string,
  name: string,
): Promise<{ id: string; name: string }> {
  const body = await request<{ model: { id: string; name: string } }>(
    `/modeler/models?bridgeId=${encodeURIComponent(bridgeId)}`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name }),
    },
  );
  return body.model;
}

/* --------------------------- bulk import --------------------------- */

export interface ImportPlanRow {
  line: number;
  id: string;
  verdict: 'create' | 'update' | 'unchanged' | 'reject';
  reason: string;
  changes: { field: string; oldValue: string | null; newValue: string | null }[];
}

export interface ImportPlan {
  applied: boolean;
  rows: ImportPlanRow[];
  unknownColumns: string[];
  counts: Record<'create' | 'update' | 'unchanged' | 'reject', number>;
  created?: number;
  updated?: number;
}

/**
 * Plan an import, or run it.
 *
 * The same call either way — `apply` is the only difference — so the report a
 * dry run shows is produced by the code that does the work, not by a separate
 * estimate that can drift from it.
 */
export async function importCsv(csv: string, apply = false): Promise<ImportPlan> {
  return request<ImportPlan>('/import', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ csv, apply }),
  });
}

/* --------------------------- history --------------------------- */

/**
 * The append-only change log for one structure.
 *
 * Its own request rather than a field on the record: the register is re-read
 * whenever anything is saved, and a growing history riding along on every one of
 * those reads is a cost paid constantly for something only the history panel
 * wants.
 */
export async function fetchChanges(bridgeId: string): Promise<BridgeChange[]> {
  const body = await request<{ changes: BridgeChange[] }>(
    `/bridges/${encodeURIComponent(bridgeId)}/changes`,
  );
  return Array.isArray(body.changes) ? body.changes : [];
}

/** Move one inspection along: draft -> review -> accepted, or back. */
export async function setInspectionState(
  bridgeId: string,
  inspectionId: string,
  state: InspectionState,
): Promise<Bridge> {
  const { bridge } = await request<{ bridge: Bridge }>(
    `/bridges/${encodeURIComponent(bridgeId)}/inspections`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ inspectionId, state }),
    },
  );
  return bridge;
}

/* --------------------------- register defects --------------------------- */

/**
 * Every defect the register holds, or every defect on one structure.
 *
 * One request for the whole network. This is what replaced a fan-out of one
 * call per structure capped at 60 — which meant the overview's "open defects"
 * was, past sixty records, a count of the first sixty.
 */
export async function fetchRegisterDefects(bridgeId?: string): Promise<RegisterDefect[]> {
  const query = bridgeId ? `?bridgeId=${encodeURIComponent(bridgeId)}` : '';
  const body = await request<{ defects: RegisterDefect[] }>(`/defects${query}`);
  return Array.isArray(body.defects) ? body.defects : [];
}

/**
 * Record a defect against a structure.
 *
 * `clientId` is minted here rather than by the server, which is what makes the
 * call safe to repeat: the same id posted twice returns the same row — and, when
 * the defect carries a photograph, stores that photograph exactly once.
 */
export async function createDefect(input: {
  bridgeId: string;
  clientId: string;
  code: string;
  severity: string;
  loggedAt: string;
  /* Where on the structure, from the model's own component tree — see
     src/screens/defectDialog.ts. `element` is the coarse DoR-inventory location
     the dialog no longer asks for, still accepted by the endpoint for the rows
     that carry one, and deliberately not offered here. */
  component?: string | null;
  /** The model's id for the component, beside its label (LC-14). */
  componentId?: string | null;
  note?: string;
  /**
   * Workflow status, when it is not the default `Open`.
   *
   * The Defect Inspector can record something already dealt with — a spall seen,
   * patched and photographed on one visit — and splitting that into a create and
   * a PATCH would leave a window in which the register says otherwise.
   */
  status?: string;
  /**
   * Pin anchor in world metres, `[x, y, z]`, when the defect was placed by
   * tapping the geometry rather than by naming a component.
   *
   * The frame is the modeller's own local one, which is the frame the exported
   * `.glb` is in — so a point raycast against that geometry is directly storable
   * and directly redrawable. Omitted for a defect chosen from a list: there is no
   * exact point, and inventing one would put a marker somewhere nobody looked.
   */
  pin?: readonly [number, number, number];
  /** Surface normal at the pin. Meaningless without one, and dropped with it. */
  normal?: readonly [number, number, number];
  /**
   * One photograph of the defect, as a base64 data URL, downscaled by
   * src/core/downscale.ts before it gets here.
   *
   * ON THIS REQUEST rather than on one of its own, for the reason addInspection
   * gives below: the finding and the evidence for it are one thing to record and
   * one thing for the queue to replay, and a two-step upload has a failure mode
   * where the bytes are in the bucket and the defect was never written.
   *
   * ONE, not several. A defect is a single finding in a single place; several
   * photographs of a structure on one visit is an inspection, and that is the
   * form that takes eight. The column is singular for the same reason.
   */
  image?: string | null;
  /**
   * How big it is, and how that was established.
   *
   * Omitted entirely where nothing was measured, so an ordinary defect's POST —
   * and the queue entry behind it — is byte-identical to what it was before the
   * form had these boxes. Refused by the endpoint if it carries a number without
   * a method, or a measurement this category does not take.
   */
  quantity?: DefectQuantity;
  /** The inspection that found it (LC-62). */
  inspectionId?: string;
}): Promise<RegisterDefect> {
  const { defect } = await request<{ defect: RegisterDefect }>('/defects', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return defect;
}

/* --------------------------- district rates --------------------------- */

/** A rate table as the register holds it, with who loaded it and when. */
export interface StoredRateTable extends RateTable {
  id: string;
  loadedBy: string | null;
  loadedAt: string;
}

/**
 * Every approved district rate table.
 *
 * ALL of them, including superseded years, because an estimate names the table
 * it used and a reader has to be able to see that last year's is still the only
 * one loaded. An empty array is the ordinary state and is not an error: this
 * register ships with no rates at all.
 */
export async function fetchRateTables(): Promise<StoredRateTable[]> {
  const body = await request<{ tables: StoredRateTable[] }>('/rates');
  return Array.isArray(body.tables) ? body.tables : [];
}

/** Load or replace the approved table for one district and fiscal year. */
export async function putRateTable(table: RateTable): Promise<StoredRateTable> {
  const body = await request<{ table: StoredRateTable }>('/rates', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(table),
  });
  return body.table;
}

/** Withdraw a rate table. Its rates go with it. */
export async function deleteRateTable(id: string): Promise<void> {
  await request<unknown>(`/rates?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/* --------------------------- the wording of recommended work --------------------------- */

/** Every treatment the administrator has reworded — migration 0047. */
export async function fetchTreatmentWording(): Promise<StoredWording[]> {
  const body = await request<{ wording: StoredWording[] }>('/treatments');
  return Array.isArray(body.wording) ? body.wording : [];
}

/** Reword one treatment. Answers with every rewording, as a GET would. */
export async function putTreatmentWording(input: {
  treatment: string;
  label: string;
  note: string;
}): Promise<StoredWording[]> {
  const body = await request<{ wording: StoredWording[] }>('/treatments', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return body.wording;
}

/** Restore one treatment's built-in wording. */
export async function deleteTreatmentWording(treatment: string): Promise<StoredWording[]> {
  const body = await request<{ wording: StoredWording[] }>(
    `/treatments?treatment=${encodeURIComponent(treatment)}`,
    { method: 'DELETE' },
  );
  return body.wording;
}

/* --------------------------- people and roles --------------------------- */

/**
 * One entry in the append-only admin log — migration 0020.
 *
 * `access` is the Cloudflare policy (who may reach the register at all);
 * `role` is this register's own table (what they may do once in). Kept apart
 * because conflating them is the commonest misunderstanding about this
 * application.
 */
export interface AdminLogEntry {
  id: string;
  kind: 'access' | 'role';
  action: string;
  subject: string;
  detail: string | null;
  actor: string | null;
  at: string;
}

/** Every account, as the People screen shows them. */
export interface PeopleReport {
  people: Person[];
  /** What has been done to roles and access lately, newest first. */
  log: AdminLogEntry[];
  /**
   * How many administrators this deployment carries in ADMIN_EMAILS.
   *
   * Fetched because the screen has to distinguish two lists that look identical:
   * one with no administrator in it on a deployment that has one anyway, and one
   * on a register nobody can administer at all.
   */
  deploymentAdmins: number;
}

/**
 * The people list. Administrators only — everybody else gets a 403 from the
 * route table, which is the correct answer and not a failure to report as one.
 */
export async function fetchPeople(): Promise<PeopleReport> {
  const body = await request<{
    people: Person[];
    log: AdminLogEntry[];
    deploymentAdmins: number;
  }>('/users');
  return {
    people: Array.isArray(body.people) ? body.people : [],
    log: Array.isArray(body.log) ? body.log : [],
    deploymentAdmins: body.deploymentAdmins ?? 0,
  };
}

/* --------------------------- signing in --------------------------- */

export interface SignedIn {
  username: string;
  /** The register the session is in. */
  tenant?: string;
  roles: Role[];
  /** True where the password was issued by an administrator and must be replaced. */
  mustChange: boolean;
  expiresAt: string;
}

/**
 * Sign in with a username and password.
 *
 * The session is a cookie the browser keeps and this code never sees: it is
 * HttpOnly, so there is nothing here to store, attach or accidentally log. Every
 * later call carries it because it is same-origin — which is also why nothing in
 * this module has an Authorization header.
 *
 * `tenant` is the register to sign in to — the one this window is for. The
 * session is that register's alone; omitted, the server uses the account's own.
 */
export async function signIn(username: string, password: string, tenant?: string | null): Promise<SignedIn> {
  return request<SignedIn>('/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ username, password, ...(tenant ? { tenant } : {}) }),
  });
}

/** End this browser's session. Succeeds even when there was not one. */
export async function signOutRequest(): Promise<void> {
  await request<unknown>('/auth/logout', { method: 'POST' });
}

/** Change your own password. The current one is required — a session is not enough. */
export async function changePassword(current: string, next: string): Promise<void> {
  await request<unknown>('/auth/password', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ current, next }),
  });
}

/**
 * Issue somebody a new password, as an administrator.
 *
 * Returns it once. The register keeps only a hash, so an administrator who loses
 * it before passing it on issues another — which costs nothing, and is the
 * correct answer rather than a register that can tell you somebody's password.
 */
export async function issuePassword(email: string): Promise<{ email: string; password: string; note: string }> {
  return request<{ email: string; password: string; note: string }>('/users/password', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

/** Create an account, or change one. The roles sent replace the roles held. */
export async function putPerson(person: {
  email: string;
  roles: Role[];
  suspended: boolean;
  fullName?: string | null;
  division?: string | null;
  note?: string | null;
}): Promise<Person> {
  const body = await request<{ person: Person }>('/users', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(person),
  });
  return body.person;
}

/**
 * Remove somebody's account.
 *
 * Not the same as denying them access — without one they hold no roles and can
 * still read. The endpoint returns the sentence that says so, and the screen
 * shows it.
 */
export async function deletePerson(email: string): Promise<string> {
  const body = await request<{ note?: string }>(`/users?email=${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });
  return body.note ?? 'Removed.';
}

/**
 * Withdraw a defect.
 *
 * Not a correction — the register is write-once about what a defect WAS, and the
 * way to change a finding is to record a new one. This is for a row that should
 * not have been written: a duplicate, the wrong structure, a test entry. The row
 * survives for the change log; it leaves every list.
 *
 * Safe to repeat, which is what lets a queue replay it: a second withdrawal keeps
 * the first one's author and date and still answers 204.
 */
export async function withdrawDefect(id: string): Promise<void> {
  await request<unknown>(`/defects/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * Move a defect along (LC-62): forward, or back to Open as a reopen with a
 * reason as `note`. Repaired carries `repairedOn` and `repairRef`.
 */
export async function updateDefectStatus(
  id: string,
  status: string,
  extra: { note?: string; repairedOn?: string; repairRef?: string } = {},
): Promise<RegisterDefect> {
  const { defect } = await request<{ defect: RegisterDefect }>(
    `/defects/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ status, ...extra }),
    },
  );
  return defect;
}

/** A defect's whole history: its status moves, and every later visit that saw it (LC-62). */
export async function fetchDefectHistory(id: string): Promise<DefectHistory> {
  return request<DefectHistory>(`/defects/${encodeURIComponent(id)}/history`);
}

/** Record that a later inspection saw a defect again, and what it measured (LC-62). */
export async function recordDefectSighting(
  id: string,
  body: { inspectionId: string; severity: string; note?: string; quantity?: DefectQuantity },
): Promise<DefectSighting> {
  const { sighting } = await request<{ sighting: DefectSighting }>(
    `/defects/${encodeURIComponent(id)}/observations`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  return sighting;
}

/** Save an edit to a bridge's inventory record from the detail screen. */
/** A new structure: its asset id and the whole inventory record. Refused if the id is taken. */
export async function createBridge(record: Record<string, unknown>): Promise<Bridge> {
  const { bridge } = await request<{ bridge: Bridge }>('/bridges', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(record),
  });
  return bridge;
}

/** One row of a bridge's phase history (LC-10), as GET /api/bridges/:id/phase serves it. */
export interface PhaseTransition {
  id: string;
  bridgeId: string;
  seq: number;
  fromPhase: Phase | null;
  toPhase: Phase;
  restricted: boolean;
  at: string;
  by: string | null;
  reason: string | null;
  causeKind: string;
  causeId: string | null;
  /** The works open after this row (LC-81), and a `register` row's in-service date (LC-80). */
  worksKind?: WorksKind | null;
  inServiceOn?: string | null;
  /** The day it took effect, where that is not the day it was recorded (migration 0046). */
  effectiveOn?: string | null;
}

export interface PhaseReport {
  current: PhaseState;
  history: PhaseTransition[];
}

/** A bridge's phase and every transition it has made, oldest first. */
export async function fetchPhase(bridgeId: string): Promise<PhaseReport> {
  return request<PhaseReport>(`/bridges/${encodeURIComponent(bridgeId)}/phase`);
}

/** A decision on a bridge's phase: restrict, lift, close or reopen, with its reason. */
export async function decidePhase(bridgeId: string, act: PhaseAct, reason: string): Promise<PhaseReport> {
  return request<PhaseReport>(`/bridges/${encodeURIComponent(bridgeId)}/phase`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ act, reason }),
  });
}

/**
 * Record a bridge already built as in service (LC-80), from a day or a year of
 * completion — or, with none, the year on its record.
 */
export async function recordInService(bridgeId: string, inServiceOn: string | null): Promise<PhaseReport> {
  return request<PhaseReport>(`/bridges/${encodeURIComponent(bridgeId)}/phase`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ act: 'register', ...(inServiceOn ? { inServiceOn } : {}) }),
  });
}

/** Several at once: the register's bulk action. Those already recorded are skipped, and say so. */
export async function recordManyInService(
  ids: readonly string[],
  inServiceOn: string | null = null,
): Promise<{ recorded: string[]; skipped: { id: string; why: string }[] }> {
  return request<{ recorded: string[]; skipped: { id: string; why: string }[] }>('/bridges/in-service', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ids, ...(inServiceOn ? { inServiceOn } : {}) }),
  });
}

/** What a structure's model says about its inventory (LC-83): the model of record, else the newest. */
export async function fetchModelInventory(bridgeId: string): Promise<{
  modelId: string | null;
  fromRecord: boolean;
  fill: { values: Partial<Record<HandoverFieldKey, number | string>>; superstructure?: string; notes: string[] } | null;
}> {
  return request(`/bridges/${encodeURIComponent(bridgeId)}/model-inventory`);
}

/** Set, or with null clear, the structure's underwater inspection interval (LC-60). */
export async function putUnderwaterInterval(bridgeId: string, months: number | null): Promise<void> {
  await request<unknown>(`/bridges/${encodeURIComponent(bridgeId)}/underwater-interval`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ months }),
  });
}

/**
 * A bridge's handover, what holds it, and what it would carry over (LC-11) —
 * or, with works open on a bridge in service, their completion (LC-82).
 */
export interface HandoverView {
  /** Which of the two the server answered with. Absent: a handover. */
  kind?: HandoverKind;
  handover: Handover | null;
  blocking: SiteItem[];
  carry: HandoverCarry[];
}

export async function fetchHandover(bridgeId: string): Promise<HandoverView> {
  return request<HandoverView>(`/bridges/${encodeURIComponent(bridgeId)}/handover`);
}

/** Save or prepare it. The source of each value is the server's to derive. */
export async function saveHandover(
  bridgeId: string,
  body: {
    completedOn: string | null;
    dlpMonths: number | null;
    modelId: string | null;
    inventory: Partial<Record<HandoverFieldKey, FieldValue>> | null;
    carry: { id: string; code: string | null; severity: string | null }[];
    waivers: { itemId: string; reason: string }[];
    note: string;
    prepare: boolean;
  },
): Promise<Handover> {
  const { handover } = await request<{ handover: Handover }>(`/bridges/${encodeURIComponent(bridgeId)}/handover`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handover;
}

/** Accept it, or return it to be prepared again. */
export async function actOnHandover(bridgeId: string, act: 'accept' | 'return', reason = ''): Promise<Handover> {
  const { handover } = await request<{ handover: Handover }>(`/bridges/${encodeURIComponent(bridgeId)}/handover`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ act, reason }),
  });
  return handover;
}

/** Make a model the bridge's model of record (LC-12). The kind follows the phase. */
export async function putModelOfRecord(bridgeId: string, modelId: string): Promise<void> {
  await request<unknown>(`/bridges/${encodeURIComponent(bridgeId)}/model-of-record`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ modelId }),
  });
}

export async function updateBridge(id: string, edit: Record<string, unknown>): Promise<Bridge> {
  const { bridge } = await request<{ bridge: Bridge }>(`/bridges/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(edit),
  });
  return bridge;
}

/**
 * Append one inspection to a bridge's history, from the detail screen.
 *
 * Photographs ride along as base64 data URLs on the same request rather than
 * going up separately — an inspection either happened or it did not, and one
 * atomic call is also what an offline queue will need to replay. The server
 * writes the bytes to R2 and stores only their metadata; see
 * functions/api/bridges/[id]/inspections.ts.
 */
export async function addInspection(
  id: string,
  insp: Inspection,
  photos: readonly string[] = [],
  clientId?: string,
): Promise<Bridge> {
  const { bridge } = await request<{ bridge: Bridge }>(`/bridges/${encodeURIComponent(id)}/inspections`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      ...insp,
      ...(clientId ? { clientId } : {}),
      ...(photos.length ? { photos } : {}),
    }),
  });
  return bridge;
}

/**
 * URL of one photograph taken at an inspection.
 *
 * As defectPhotoUrl, and for the same reason: served on its own URL so the
 * browser fetches it once instead of carrying it inline on every read of the
 * register. Unlike a defect photograph this one is immutable — an inspection is
 * recorded once and nothing replaces its photographs — so the response is
 * cached for a year rather than revalidated.
 */
export function inspectionPhotoUrl(bridgeId: string, photoId: string): string {
  const query = new URLSearchParams({ bridgeId, photoId });
  return `${BASE}/inspection-photo?${query}`;
}

/** Delete one saved model, its viewport still and the cost assumptions keyed to it. */
export async function deleteModel(bridgeId: string, modelId: string): Promise<void> {
  const query = new URLSearchParams({ bridgeId, modelId });
  await request<{ deleted: boolean }>(`/modeler/models?${query}`, { method: 'DELETE' });
}

/**
 * URL of a model's viewport still.
 *
 * Built rather than fetched — it goes straight into an `<img src>` so the browser
 * caches and revalidates it, which is the whole reason the bytes are served as an
 * image instead of travelling as base64 inside the model index.
 */
export function modelThumbUrl(bridgeId: string, modelId: string, nonce = 0): string {
  const query = new URLSearchParams({ bridgeId, modelId });
  /* Only once something is known to have replaced the still — an import — and
     never on the ordinary path, where the whole point is that the browser reuses
     what it has. The endpoint sends `cache-control: no-cache`, which buys a
     revalidation on a fresh request but not a fresh request: this URL does not
     change when a model is re-thumbnailed, so an <img> or a background-image
     already showing the old bytes has no reason to ask again. The nonce is that
     reason. Ignored by the endpoint, which reads only bridgeId and modelId. */
  if (nonce > 0) query.set('v', String(nonce));
  return `${BASE}/modeler/thumb?${query}`;
}

/**
 * URL of a model's exported geometry, as binary glTF.
 *
 * Built rather than fetched, like the thumbnail: three.js's loader takes a URL and
 * does its own request, and the browser caches the bytes. Only ask for one the
 * model index reported `hasGeometry` for — the endpoint 404s the rest, which is the
 * ordinary state of a model nobody has looked at in 3D.
 */
export function modelGeometryUrl(
  bridgeId: string,
  modelId: string,
  /**
   * The model index's `geometryVersion` — the exported bytes' own fingerprint.
   *
   * WITH IT, THE URL NAMES ONE FILE FOR EVER, and the endpoint says so: a
   * re-export changes the etag, which changes this, which is a different URL, so
   * the answer can carry a year of `immutable` caching instead of a revalidation
   * on every fetch. Without it the URL is the stable one it has always been and
   * the endpoint keeps answering `no-cache` — which is what a caller with no
   * index in hand gets, and is correct rather than merely tolerated.
   *
   * The endpoint VERIFIES it rather than trusting it; see the note there for why
   * a stale version must never be allowed to pin bytes in a shared cache.
   */
  version?: string | null,
): string {
  const query = new URLSearchParams({ bridgeId, modelId });
  if (version) query.set('v', version);
  return `${BASE}/modeler/geometry?${query}`;
}

/**
 * Store the export document an imported model was made from, so the model exists
 * on the server and not only in the browser that imported it.
 *
 * WHAT IT FIXES: the mesh lives in the vendored io/MeshStore.ts, which is
 * IndexedDB, which is scoped to the ORIGIN. So an imported model opened anywhere
 * else — another engineer's laptop, or the same laptop with the dev server on a
 * different port — came up in viewer mode with no triangles and no explanation.
 * See functions/api/modeler/source.ts for the whole account.
 *
 * Sends the texts exactly as the frame's `import-csibridge` message takes them: an
 * array, because an import can be a bundle plus an alignment. Gzipped where the
 * browser can, because the payload is JSON full of repeated float text and this is
 * a tool for people on one bar of signal — same fallback as the storage shim, so a
 * browser without CompressionStream sends it plain and the endpoint sniffs which
 * arrived.
 *
 * AND IT COMES BACK WITH THE GEOMETRY, which is why this returns a shape rather
 * than a number now. The endpoint derives the model's `.glb` from the same document
 * it is storing (functions/lib/meshGlb.ts), so by the time this resolves the thing
 * every device draws already exists — and the import knows not to go and build it a
 * second time out of a running modeller. `geometry: null` with a `reason` is the
 * ordinary answer for an export with no mesh in it.
 */
export async function putModelSource(
  bridgeId: string,
  modelId: string,
  texts: readonly string[],
): Promise<StoredSource> {
  const query = new URLSearchParams({ bridgeId, modelId });
  const plain = JSON.stringify(texts);
  const packed = await deflate(plain);
  const body = await request<{
    bytes?: unknown;
    geometry?: { bytes?: unknown; parts?: unknown; triangles?: unknown } | null;
    geometryReason?: unknown;
  }>(`/modeler/source?${query}`, {
    method: 'PUT',
    headers: packed
      ? { 'content-type': 'application/json', 'content-encoding': 'gzip' }
      : { 'content-type': 'application/json' },
    body: packed ?? plain,
  });

  /* Read field by field rather than cast. This is the one response the import path
     branches on — whether to drive a modeller at all — and a deployment where the
     endpoint predates the derivation answers without the field. Absent reads as
     "nothing was derived", which takes the old path: slower and weaker, but not
     wrong. */
  const g = body.geometry;
  return {
    bytes: typeof body.bytes === 'number' ? body.bytes : plain.length,
    geometry:
      g && typeof g.bytes === 'number'
        ? {
            bytes: g.bytes,
            parts: typeof g.parts === 'number' ? g.parts : 0,
            triangles: typeof g.triangles === 'number' ? g.triangles : 0,
          }
        : null,
    geometryReason: typeof body.geometryReason === 'string' ? body.geometryReason : null,
  };
}

/** What storing an export produced — see putModelSource. */
export interface StoredSource {
  /** Uncompressed size of the stored document. */
  bytes: number;
  /** The geometry derived from it, or null where none was. */
  geometry: { bytes: number; parts: number; triangles: number } | null;
  /** Why no geometry was derived, or null when some was. */
  geometryReason: string | null;
}

/**
 * Read back a stored source export, as the array of texts it was stored as.
 *
 * Only ask for one the model index reported `hasSource` for; the endpoint 404s the
 * rest, which is the ordinary state of a parametric model — it has no source and
 * needs none, being rebuilt from its own parameters wherever it is opened.
 */
export async function fetchModelSource(
  bridgeId: string,
  modelId: string,
): Promise<string[]> {
  const query = new URLSearchParams({ bridgeId, modelId });
  const texts = await request<unknown>(`/modeler/source?${query}`);
  if (!Array.isArray(texts) || texts.some((t) => typeof t !== 'string')) {
    throw new Error('The stored source export is not an array of texts.');
  }
  return texts as string[];
}

/**
 * Gzip a string, or null where the browser cannot.
 *
 * Lifted from the storage shim's own upload path (src/modeler-host/boot.ts) and
 * kept deliberately best-effort: compression is a saving, not a requirement, and
 * the endpoint accepts either. A browser with no `CompressionStream` — or one that
 * throws inside it — must still be able to store a model.
 */
async function deflate(text: string): Promise<Uint8Array | null> {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    return null;
  }
}

/**
 * Store a model's exported geometry. Returns the size the server accepted.
 *
 * The bytes come from a hidden modeller over postMessage — see
 * src/modeler/geometry.ts — and go up as-is: a .glb is already a binary container,
 * and the endpoint checks its magic word rather than trusting the caller.
 */
export async function putModelGeometry(
  bridgeId: string,
  modelId: string,
  glb: ArrayBuffer,
): Promise<number> {
  const query = new URLSearchParams({ bridgeId, modelId });
  const body = await request<{ bytes: number }>(`/modeler/geometry?${query}`, {
    method: 'PUT',
    headers: { 'content-type': 'model/gltf-binary' },
    body: glb,
  });
  return typeof body.bytes === 'number' ? body.bytes : glb.byteLength;
}

/**
 * URL of the photograph attached to one defect, by the handle the modeller knows
 * it as — `RegisterDefect.modelDefectId`, not the row's own id.
 *
 * As modelThumbUrl, and for the same reason: an inspector's photograph is served
 * as an image so the browser can cache it, rather than riding along in a defect
 * list that is re-read whenever the selection moves. Only ask for one a list
 * reported `hasPhoto` for — the endpoint 404s the rest.
 *
 * The same string is built server-side by `modelerPhotoUrl` in
 * functions/api/modeler/defects.ts, which is what the projection puts on a
 * record's `image` for the modeller to load. Duplicated rather than shared,
 * because importing this module into a Pages Function would pull the whole client
 * into a Worker bundle; `npm test` asserts the two agree.
 */
export function defectPhotoUrl(bridgeId: string, modelDefectId: number, n = 1): string {
  /* The first photograph keeps the URL it has always had — the one the modeller
     is handed — so `n` is only spelled out for the second and later. */
  const query = new URLSearchParams({
    bridgeId,
    defectId: String(modelDefectId),
    ...(n > 1 ? { n: String(n) } : {}),
  });
  return `${BASE}/modeler/defect-photo?${query}`;
}

/**
 * Where to load photograph `n` of a defect from: its own demonstration file where
 * it carries one (`RegisterDefect.photoUrls`), else `defectPhotoUrl`. `thumb` asks
 * for the smaller copy where there is one; the register serves one size.
 */
export function defectPhotoSrc(
  d: Pick<RegisterDefect, 'bridgeId' | 'modelDefectId' | 'photoUrls' | 'photoThumbUrls'>,
  n = 1,
  thumb = false,
): string {
  const own = (thumb ? d.photoThumbUrls?.[n - 1] : undefined) ?? d.photoUrls?.[n - 1];
  return own ?? defectPhotoUrl(d.bridgeId, d.modelDefectId, n);
}

/* --------------------------- the construction record --------------------------- */

/** What both progress reads return: the rows, and what they add up to. */
export interface ProgressReport {
  events: ProgressEntry[];
  summary: ProgressSummary;
}

/**
 * The summary an unreachable register produces.
 *
 * Zero of everything and no units, which is what `summariseProgress([])`
 * returns on the server for an empty set — so a screen drawing a failed read
 * and a screen drawing a genuinely empty week go down the same path instead of
 * one of them having to cope with a null.
 */
const NO_PROGRESS: ProgressReport = {
  events: [],
  summary: { count: 0, draft: 0, structures: 0, quantities: [] },
};

/**
 * One structure's construction record, newest first.
 *
 * Scoped by the endpoint through the bridge: a structure in another
 * department's register 404s rather than returning an empty record, which would
 * read identically to a bridge nobody has started work on.
 */
export async function fetchProgress(bridgeId: string): Promise<ProgressReport> {
  const query = new URLSearchParams({ bridgeId });
  const body = await request<Partial<ProgressReport>>(`/progress?${query}`);
  return {
    events: Array.isArray(body.events) ? body.events : [],
    summary: body.summary ?? NO_PROGRESS.summary,
  };
}

/**
 * The whole department's record over a period — the weekly, monthly and
 * quarterly reports, which are the same rows asked a different way.
 *
 * Both dates inclusive, which is how somebody asks for "the week of the 8th to
 * the 14th" and not how a half-open range behaves. The endpoint refuses a call
 * that names both a structure and a period, so these are two functions rather
 * than one with optional arguments.
 */
export async function fetchProgressPeriod(from: string, to: string): Promise<ProgressReport> {
  const query = new URLSearchParams({ from, to });
  const body = await request<Partial<ProgressReport>>(`/progress?${query}`);
  return {
    events: Array.isArray(body.events) ? body.events : [],
    summary: body.summary ?? NO_PROGRESS.summary,
  };
}

/**
 * Record something that was done on a structure.
 *
 * `clientId` is minted by the caller rather than here, for the reason
 * `createDefect` takes one: the id has to be the same whether this lands now or
 * from the queue in an hour, and the same id posted twice returns the same row
 * — storing the photograph exactly once.
 *
 * THE UNIT IS NOT SENT. It is the library's for the activity, the endpoint
 * looks it up, and a caller naming a different one is refused — so there is
 * nothing for this signature to offer and no way for a screen to get it wrong.
 */
export async function createProgress(input: {
  bridgeId: string;
  clientId: string;
  /** An id from src/data/activities.ts. */
  activity: string;
  /** ISO date the work happened, which may not be today. */
  occurredOn: string;
  element?: string | null;
  component?: string | null;
  /** The model's id for the component, beside its label (LC-14). */
  componentId?: string | null;
  /**
   * How much, where the activity is measured in something and somebody
   * measured it. Omitted entirely otherwise: a foreman who poured "the north
   * half of the bent cap" and did not measure it should still be able to say
   * the pour happened.
   */
  quantity?: number;
  note?: string;
  /**
   * One photograph, as a base64 data URL, downscaled before it gets here.
   *
   * ON THIS REQUEST rather than on one of its own, for the reason a defect's
   * is: the entry and the evidence for it are one thing to record and one thing
   * for the queue to replay, and a two-step upload has a failure mode where the
   * bytes are in the bucket and the pour was never written.
   */
  image?: string | null;
}): Promise<ProgressEntry> {
  const { event } = await request<{ event: ProgressEntry }>('/progress', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return event;
}

/**
 * Move one construction entry along: draft -> review -> accepted, or back.
 *
 * The state is the only mutable field there is. What an entry says was said
 * standing on a site, and a record an office can rewrite is a draft of one — a
 * pour entered against the wrong pier is corrected by a second entry and a
 * note, so the correction is part of the history.
 */
export async function setProgressState(
  id: string,
  state: ProgressState,
): Promise<ProgressEntry> {
  const { event } = await request<{ event: ProgressEntry }>(`/progress/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ state }),
  });
  return event;
}

/**
 * URL of the photograph attached to one construction entry.
 *
 * As inspectionPhotoUrl, and immutable for the same reason: a replayed POST
 * never overwrites a photograph and there is no route that replaces one, so the
 * bytes behind a given id cannot change and the response is cached for a year
 * rather than revalidated. Only ask for one an entry reported `hasPhoto` for —
 * the endpoint 404s the rest.
 */
export function progressPhotoUrl(bridgeId: string, eventId: string): string {
  const query = new URLSearchParams({ bridgeId, eventId });
  return `${BASE}/progress-photo?${query}`;
}

/* ------------------- the daily return, safety and documents ------------------- */

/** A structure's period report — its executive summary and chosen photographs — or null (LC-55). */
export async function fetchPeriodReport(bridgeId: string, from: string, to: string): Promise<PeriodReport | null> {
  const query = new URLSearchParams({ bridgeId, from, to });
  const body = await request<{ report?: PeriodReport | null }>(`/period-reports?${query}`);
  return body.report ?? null;
}

/** Write a structure's period report: the summary, and the photographs where they are sent (LC-55). */
export async function savePeriodReport(input: {
  bridgeId: string;
  from: string;
  to: string;
  summary: string;
  photoIds?: string[] | null;
}): Promise<PeriodReport> {
  const { report } = await request<{ report: PeriodReport }>('/period-reports', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return report;
}

/** URL of the photograph on a daily return or an incident (LC-56). */
export function sitePhotoUrl(bridgeId: string, kind: 'return' | 'incident', id: string): string {
  const query = new URLSearchParams({ bridgeId, kind, id });
  return `${BASE}/site-photo?${query}`;
}

/** One structure's site diary, newest day first. */
export async function fetchSiteReturns(bridgeId: string): Promise<SiteReturn[]> {
  const query = new URLSearchParams({ bridgeId });
  const body = await request<{ returns?: SiteReturn[] }>(`/site-returns?${query}`);
  return Array.isArray(body.returns) ? body.returns : [];
}

/** A department's diary over a period, inclusive at both ends. */
export async function fetchSiteReturnsPeriod(from: string, to: string): Promise<SiteReturn[]> {
  const query = new URLSearchParams({ from, to });
  const body = await request<{ returns?: SiteReturn[] }>(`/site-returns?${query}`);
  return Array.isArray(body.returns) ? body.returns : [];
}

/**
 * Write a day's return, or correct one that is still a draft.
 *
 * THE RESPONSE CAN SAY THE WRITE DID NOT HAPPEN, and the caller has to look.
 * A day whose return has been accepted is frozen — it has been reported — so
 * the endpoint returns the stored row with `frozen: true` rather than a 4xx:
 * an offline queue must treat that as done rather than retry it for ever, and
 * the screen has to be able to tell somebody their correction was too late.
 */
export async function putSiteReturn(input: {
  bridgeId: string;
  clientId: string;
  returnedOn: string;
  weather: string;
  labour?: number | null;
  labourNote?: string;
  plant?: string;
  hours?: number | null;
  note?: string;
  /** LC-56: people by trade (the total is their sum), plant working and idle, rain, a photograph. */
  labourByTrade?: TradeCount[];
  plantRows?: PlantDay[];
  rainfallMm?: number | null;
  image?: string;
}): Promise<{ entry: SiteReturn; frozen: boolean }> {
  const body = await request<{ return: SiteReturn; frozen?: boolean }>('/site-returns', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return { entry: body.return, frozen: body.frozen === true };
}

/** Move a daily return through review. Accepting it also freezes its figures. */
export async function setSiteReturnState(id: string, state: ProgressState): Promise<SiteReturn> {
  const body = await request<{ return: SiteReturn }>(`/site-returns/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ state }),
  });
  return body.return;
}

export async function fetchIncidents(bridgeId: string): Promise<SiteIncident[]> {
  const query = new URLSearchParams({ bridgeId });
  const body = await request<{ incidents?: SiteIncident[] }>(`/site-incidents?${query}`);
  return Array.isArray(body.incidents) ? body.incidents : [];
}

export async function fetchIncidentsPeriod(from: string, to: string): Promise<SiteIncident[]> {
  const query = new URLSearchParams({ from, to });
  const body = await request<{ incidents?: SiteIncident[] }>(`/site-incidents?${query}`);
  return Array.isArray(body.incidents) ? body.incidents : [];
}

/** Report an incident. The description is required; a kind and a date are a tally mark. */
export async function createIncident(input: {
  bridgeId: string;
  clientId: string;
  kind: string;
  occurredOn: string;
  description: string;
  element?: string | null;
  action?: string;
  /** LC-56: who it involved, the days lost, a photograph. */
  peopleInvolved?: number | null;
  peopleNames?: string;
  daysLost?: number | null;
  image?: string;
}): Promise<SiteIncident> {
  const { incident } = await request<{ incident: SiteIncident }>('/site-incidents', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return incident;
}

/** Record what was done about an incident, and whether that is finished. */
export async function closeIncident(
  id: string,
  action: string,
  closed: boolean,
  /** The close-out day, required with a close (LC-50). */
  closedOn: string | null = null,
): Promise<SiteIncident> {
  const { incident } = await request<{ incident: SiteIncident }>(
    `/site-incidents/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(closed ? { action, closed, closedOn } : { action, closed }),
    },
  );
  return incident;
}

export async function fetchDocuments(bridgeId: string): Promise<SiteDocument[]> {
  const query = new URLSearchParams({ bridgeId });
  const body = await request<{ documents?: SiteDocument[] }>(`/site-documents?${query}`);
  return Array.isArray(body.documents) ? body.documents : [];
}

export async function fetchDocumentsPeriod(from: string, to: string): Promise<SiteDocument[]> {
  const query = new URLSearchParams({ from, to });
  const body = await request<{ documents?: SiteDocument[] }>(`/site-documents?${query}`);
  return Array.isArray(body.documents) ? body.documents : [];
}

/**
 * File a document against the works.
 *
 * `file` is a base64 data URL and rides on this request for the reason a
 * photograph does: the record and the bytes are one thing, and a two-step
 * upload has a failure mode where the file exists and nothing references it.
 * PDFs and images only — see src/data/documents.ts.
 */
export async function createDocument(input: {
  bridgeId: string;
  clientId: string;
  kind: string;
  title: string;
  file: string;
  filename?: string;
  eventId?: string | null;
  datedOn?: string | null;
  note?: string;
}): Promise<SiteDocument> {
  const { document } = await request<{ document: SiteDocument }>('/site-documents', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return document;
}

/**
 * URL of one filed document.
 *
 * Immutable, like a progress photograph: a revision is a second document, so
 * nothing replaces the bytes behind an id.
 */
export function documentUrl(bridgeId: string, documentId: string): string {
  const query = new URLSearchParams({ bridgeId, documentId });
  return `${BASE}/site-document?${query}`;
}

/* --------------------------- tests and the register (LC-53, LC-54) --------------------------- */

export async function fetchTests(bridgeId: string): Promise<SiteTest[]> {
  const query = new URLSearchParams({ bridgeId });
  const body = await request<{ tests?: SiteTest[] }>(`/site-tests?${query}`);
  return Array.isArray(body.tests) ? body.tests : [];
}

export async function fetchTestsPeriod(from: string, to: string): Promise<SiteTest[]> {
  const query = new URLSearchParams({ from, to });
  const body = await request<{ tests?: SiteTest[] }>(`/site-tests?${query}`);
  return Array.isArray(body.tests) ? body.tests : [];
}

/**
 * Record a test result. The pass is the register's to derive; a failure comes
 * back with the NCR it raised.
 */
export async function createTest(input: Record<string, unknown> & {
  bridgeId: string;
  clientId: string;
  kind: string;
  testedOn: string;
}): Promise<{ test: SiteTest; ncr: SiteItem | null }> {
  const body = await request<{ test: SiteTest; ncr?: SiteItem | null }>('/site-tests', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return { test: body.test, ncr: body.ncr ?? null };
}

export async function fetchItems(bridgeId: string): Promise<SiteItem[]> {
  const query = new URLSearchParams({ bridgeId });
  const body = await request<{ items?: SiteItem[] }>(`/site-items?${query}`);
  return Array.isArray(body.items) ? body.items : [];
}

export async function fetchItemsPeriod(from: string, to: string): Promise<SiteItem[]> {
  const query = new URLSearchParams({ from, to });
  const body = await request<{ items?: SiteItem[] }>(`/site-items?${query}`);
  return Array.isArray(body.items) ? body.items : [];
}

/** Raise a hold point, an NCR or an RFI. The reference is the register's to number. */
export async function createItem(input: {
  bridgeId: string;
  clientId: string;
  kind: string;
  title: string;
  detail?: string;
  owner?: string | null;
  dueOn?: string | null;
  activityId?: string | null;
  activityName?: string | null;
  component?: string | null;
  componentId?: string | null;
  eventId?: string | null;
}): Promise<SiteItem> {
  const { item } = await request<{ item: SiteItem }>('/site-items', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return item;
}

/** Answer, close or release an item. */
export async function actOnItem(id: string, act: string, note: string): Promise<SiteItem> {
  const { item } = await request<{ item: SiteItem }>(`/site-items/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ act, note }),
  });
  return item;
}

/* --------------------------- the programme --------------------------- */

/**
 * The modeller's whole persisted namespace for one structure.
 *
 * READ FOR ONE KEY, and only at the moment somebody takes a baseline. The
 * modeller's own frame reads this too, through the shim in
 * src/modeler-host/boot.ts, and that is the only other caller — nothing about
 * the register's ordinary screens depends on it. See src/data/programme.ts for
 * what is pulled out and why the parse happens here rather than on the server.
 */
export async function fetchModelerState(bridgeId: string): Promise<Record<string, string>> {
  const query = new URLSearchParams({ bridgeId });
  const body = await request<{ entries?: Record<string, string> }>(`/modeler/state?${query}`);
  return body.entries ?? {};
}

/**
 * The structure's accepted baseline, or null where nobody has frozen and
 * accepted one — and beside it the revision waiting for acceptance (LC-40).
 */
export async function fetchBaseline(bridgeId: string): Promise<{ baseline: Baseline | null; proposed: Baseline | null }> {
  const query = new URLSearchParams({ bridgeId });
  const body = await request<{ baseline?: Baseline | null; proposed?: Baseline | null }>(`/baselines?${query}`);
  return { baseline: body.baseline ?? null, proposed: body.proposed ?? null };
}

/** Every baseline taken of a structure, newest first: the history (LC-40). */
export async function fetchBaselineHistory(bridgeId: string): Promise<BaselineHeader[]> {
  const query = new URLSearchParams({ bridgeId, list: '1' });
  const body = await request<{ baselines?: BaselineHeader[] }>(`/baselines?${query}`);
  return Array.isArray(body.baselines) ? body.baselines : [];
}

/** Freeze a programme, as proposed. Append-only: a revision is a new baseline, with a reason. */
export async function createBaseline(input: {
  bridgeId: string;
  name: string;
  projectStart: string;
  activities: BaselineActivity[];
  links?: BaselineLink[];
  contract: ContractHeader;
  calendarId?: string | null;
  reason?: string | null;
  modelId?: string | null;
  note?: string;
  /** On a bridge in service, the works it programmes (LC-81). */
  worksKind?: WorksKind | null;
}): Promise<Baseline> {
  const { baseline } = await request<{ baseline: Baseline }>('/baselines', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return baseline;
}

/** Accept a proposed baseline. The approver's, never the planner's who took it. */
export async function acceptBaseline(
  id: string,
): Promise<{ baseline: Baseline; started: boolean; worksOpened?: boolean }> {
  return request<{ baseline: Baseline; started: boolean; worksOpened?: boolean }>(`/baselines/${encodeURIComponent(id)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ act: 'accept' }),
  });
}

/** The department's working calendars (LC-43). */
export async function fetchCalendars(): Promise<WorkCalendar[]> {
  const body = await request<{ calendars?: WorkCalendar[] }>('/calendars');
  return Array.isArray(body.calendars) ? body.calendars : [];
}

/** Save a calendar: new with no id, an edit with one. The administrator's. */
export async function saveCalendar(input: {
  id: string | null;
  name: string;
  week: string;
  holidays: WorkCalendar['holidays'];
  windows: WorkCalendar['windows'];
}): Promise<WorkCalendar> {
  const { calendar } = await request<{ calendar: WorkCalendar }>('/calendars', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return calendar;
}

/** A structure's extensions of time (LC-42). */
export async function fetchExtensions(bridgeId: string): Promise<ContractExtension[]> {
  const query = new URLSearchParams({ bridgeId });
  const body = await request<{ extensions?: ContractExtension[] }>(`/contract-extensions?${query}`);
  return Array.isArray(body.extensions) ? body.extensions : [];
}

export async function raiseExtension(input: { bridgeId: string; reason: string; days: number }): Promise<ContractExtension> {
  const { extension } = await request<{ extension: ContractExtension }>('/contract-extensions', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return extension;
}

export async function decideExtension(id: string, act: 'accept' | 'reject'): Promise<ContractExtension> {
  const { extension } = await request<{ extension: ContractExtension }>(
    `/contract-extensions/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ act }),
    },
  );
  return extension;
}

export async function fetchScheduleMatches(bridgeId: string): Promise<ScheduleMatch[]> {
  const query = new URLSearchParams({ bridgeId });
  const body = await request<{ matches?: ScheduleMatch[] }>(`/schedule-matches?${query}`);
  return Array.isArray(body.matches) ? body.matches : [];
}

/** Confirm a match, or correct one. A match is an opinion and replaces in place. */
export async function putScheduleMatch(input: {
  bridgeId: string;
  activityId: string;
  activityName: string;
  component: string;
  /** The model's id for the component, where the entries matched carry one (LC-14). */
  componentId?: string | null;
  completedOn?: string | null;
}): Promise<ScheduleMatch> {
  const { match } = await request<{ match: ScheduleMatch }>('/schedule-matches', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return match;
}

/** Withdraw a match. Nothing about the works is lost — the two stand, unjoined. */
export async function deleteScheduleMatch(bridgeId: string, activityId: string): Promise<void> {
  const query = new URLSearchParams({ bridgeId, activityId });
  await request<{ deleted: boolean }>(`/schedule-matches?${query}`, { method: 'DELETE' });
}
